// Simple tree component that renders toctree data from window.TOCTREE_DATA
// The data is provided by toctree.js (generated by Sphinx extension)

interface TreeNode {
  id: string;
  title: string;
  type: "section" | "page";
  href?: string;
  children: TreeNode[];
}

interface TreeData {
  items: TreeNode[];
}

class ThdocsTree extends HTMLElement {
  private data: TreeData | null = null;
  private expanded = new Map<string, boolean>();
  private currentPage: string | null = null;

  connectedCallback() {
    try {
      // Kill switch for testing the no-JS / fallback-render path
      if (sessionStorage.getItem("DISABLE_THDOCS_TREE")) {
        console.log("[thdocs-tree] Disabled via sessionStorage.DISABLE_THDOCS_TREE — leaving fallback content");
        return;
      }
      console.log("[thdocs-tree] Initializing...");
      this.loadData();
    } catch (error) {
      console.error("[thdocs-tree] Error:", error);
    }
  }

  private loadData() {
    try {
      console.log("[thdocs-tree] Loading data...");

      // toctree.js sets window.TOCTREE_DATA as a global variable
      const globalData = (globalThis as any).TOCTREE_DATA;
      if (!globalData) {
        throw new Error("window.TOCTREE_DATA not found - toctree.js may not have loaded");
      }

      this.data = globalData;
      console.log("[thdocs-tree] Data loaded:", this.data);

      this.determineCurrentPage();
      this.restoreState();
      this.expandPathToCurrent();
      this.render();
      this.restoreScroll();
      this.scrollCurrentIntoView();
      this.restoreFocus();
    } catch (error) {
      console.error("[thdocs-tree] Failed to load data:", error);
    }
  }

  private determineCurrentPage() {
    // Store the absolute URL of the current page (without hash) for link comparison
    this.currentPage = window.location.href.split("#")[0];
  }

  private resolveHref(href: string): string {
    // Sphinx 7+ puts the relative path to the doc root on <html data-content_root="...">
    const baseUrl = document.documentElement.getAttribute("data-content_root") || "./";
    return baseUrl + href;
  }

  private nodeAbsoluteHref(node: TreeNode): string | null {
    if (!node.href) return null;
    return new URL(this.resolveHref(node.href), document.baseURI).href;
  }

  private expandPathToCurrent() {
    if (!this.data) return;
    const walk = (items: TreeNode[], ancestors: string[]): boolean => {
      for (const node of items) {
        if (this.nodeAbsoluteHref(node) === this.currentPage) {
          for (const id of ancestors) this.expanded.set(id, true);
          if (node.children.length > 0) this.expanded.set(node.id, true);
          return true;
        }
        if (node.children.length > 0 && walk(node.children, [...ancestors, node.id])) {
          return true;
        }
      }
      return false;
    };
    walk(this.data.items, []);
  }

  private getScrollContainer(): HTMLElement {
    return this.closest<HTMLElement>("[data-panel='contents']") || this;
  }

  private restoreScroll() {
    const saved = sessionStorage.getItem("thdocs-tree-scroll");
    if (saved === null) return;
    sessionStorage.removeItem("thdocs-tree-scroll");
    const container = this.getScrollContainer();
    container.scrollTop = parseInt(saved, 10);
  }

  private scrollCurrentIntoView() {
    const current = this.querySelector<HTMLElement>(".thdocs-tree__link--current");
    if (!current) return;
    // Only scroll if not already visible within the scroll container
    const container = this.getScrollContainer();
    const cRect = current.getBoundingClientRect();
    const pRect = container.getBoundingClientRect();
    if (cRect.top < pRect.top || cRect.bottom > pRect.bottom) {
      current.scrollIntoView({ block: "nearest", behavior: "auto" });
    }
  }

  private restoreFocus() {
    const focusHref = sessionStorage.getItem("thdocs-tree-focus");
    if (!focusHref) return;
    sessionStorage.removeItem("thdocs-tree-focus");
    const link = this.querySelector<HTMLAnchorElement>(`a.thdocs-tree__link[href="${CSS.escape(focusHref)}"]`)
      || Array.from(this.querySelectorAll<HTMLAnchorElement>("a.thdocs-tree__link")).find(
        (a) => a.href === focusHref,
      );
    if (link) {
      // Defer past Sphinx's own focus-claiming on DOMContentLoaded
      setTimeout(() => link.focus({ preventScroll: false }), 0);
    }
  }

  private restoreState() {
    const saved = sessionStorage.getItem("thdocs-tree-expanded");
    if (saved) {
      try {
        const expanded = JSON.parse(saved);
        this.expanded = new Map(expanded);
      } catch {}
    }
  }

  private saveState() {
    const expanded = Array.from(this.expanded.entries());
    sessionStorage.setItem("thdocs-tree-expanded", JSON.stringify(expanded));
  }

  private toggleNode(nodeId: string) {
    const current = this.expanded.get(nodeId) ?? false;
    this.expanded.set(nodeId, !current);
    this.saveState();
    this.render();
    // Re-render destroyed the focused button — restore focus on the new one
    const btn = this.querySelector<HTMLButtonElement>(
      `button.thdocs-tree__icon[data-node-id="${CSS.escape(nodeId)}"]`,
    );
    if (btn) btn.focus({ preventScroll: true });
  }

  private render() {
    if (!this.data) return;

    const ul = document.createElement("ul");
    ul.className = "thdocs-tree__list";

    for (const item of this.data.items) {
      ul.appendChild(this.renderNode(item));
    }

    // Replace previous render if any; otherwise append alongside the fallback
    // and mark the host so CSS can hide the fallback.
    const existing = this.querySelector(":scope > ul.thdocs-tree__list");
    if (existing) {
      existing.replaceWith(ul);
    } else {
      this.appendChild(ul);
      this.classList.add("thdocs-tree--hydrated");
    }
  }

  private renderNode(node: TreeNode): HTMLElement {
    const li = document.createElement("li");
    li.className = "thdocs-tree__item";

    const row = document.createElement("div");
    row.className = "thdocs-tree__row";

    const hasChildren = node.children.length > 0;
    const isExpanded = this.expanded.get(node.id) ?? false;

    // CHM-style icon: closed book / open book for nodes with children, page for leaves
    if (hasChildren) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = `thdocs-tree__icon ${isExpanded ? "thdocs-tree__icon--book-open" : "thdocs-tree__icon--book-closed"}`;
      btn.setAttribute("aria-expanded", isExpanded ? "true" : "false");
      btn.setAttribute("aria-label", "Toggle");
      btn.dataset.nodeId = node.id;
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.toggleNode(node.id);
      });
      row.appendChild(btn);
    } else {
      const icon = document.createElement("span");
      icon.className = "thdocs-tree__icon thdocs-tree__icon--page";
      row.appendChild(icon);
    }

    if (node.type === "section") {
      const span = document.createElement("span");
      span.className = "thdocs-tree__section-title";
      span.textContent = node.title;
      if (hasChildren) {
        span.style.cursor = "pointer";
        span.addEventListener("click", () => this.toggleNode(node.id));
      }
      row.appendChild(span);
    } else {
      const link = document.createElement("a");
      link.href = node.href ? this.resolveHref(node.href) : "#";
      // After assignment, link.href returns the resolved absolute URL — compare against current page
      const isCurrent = link.href === this.currentPage;
      link.className = `thdocs-tree__link${isCurrent ? " thdocs-tree__link--current" : ""}`;
      link.textContent = node.title;
      // Remember focus + scroll so the next page can restore them for keyboard/visual continuity
      const remember = () => {
        sessionStorage.setItem("thdocs-tree-focus", link.href);
        sessionStorage.setItem("thdocs-tree-scroll", String(this.getScrollContainer().scrollTop));
      };
      link.addEventListener("click", remember);
      link.addEventListener("keydown", (e) => {
        if (e.key === "Enter") remember();
      });
      row.appendChild(link);
    }

    li.appendChild(row);

    if (hasChildren) {
      const ul = document.createElement("ul");
      ul.className = "thdocs-tree__children";
      ul.style.display = isExpanded ? "block" : "none";

      for (const child of node.children) {
        ul.appendChild(this.renderNode(child));
      }

      li.appendChild(ul);
    }

    return li;
  }

}

// Register the custom element
if (!customElements.get("thdocs-tree")) {
  customElements.define("thdocs-tree", ThdocsTree);
}
